import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Check,
  Cloud,
  FileImage,
  FolderSync,
  Image as ImageIcon,
  Loader2,
  LogOut,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  UploadCloud
} from "lucide-react";
import { clearCurrentUser, isRealCurrentUser, loadCurrentUser, signInWithApple, signInWithGoogle } from "./auth";
import {
  API_ORIGIN,
  connectCloud,
  getCloudConfig,
  getCloudConnections,
  getHealth,
  getManuals,
  importManuals,
  processManual,
  saveSyncNote,
  searchManuals,
  uploadManual
} from "./api";
import { createAppleCloudKitAdapter } from "./cloud/appleCloudKit";
import { createGoogleDriveAdapter } from "./cloud/googleDrive";
import type { CloudAdapter } from "./cloud/types";
import type { CloudConfig, CloudConnection, CurrentUser, Manual, RuntimeStatus, UploadTarget } from "./types";

const adsenseClient = import.meta.env.VITE_ADSENSE_CLIENT;

function loadAdSense() {
  if (!adsenseClient || document.querySelector("[data-allmanual-adsense]")) return;
  const script = document.createElement("script");
  script.async = true;
  script.crossOrigin = "anonymous";
  script.dataset.allmanualAdsense = "true";
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseClient}`;
  document.head.appendChild(script);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function providerLabel(provider: string) {
  if (provider === "google") return "Google Drive";
  if (provider === "apple") return "Apple iCloud";
  return "Local";
}

export function App() {
  const [health, setHealth] = useState<RuntimeStatus | null>(null);
  const [cloudConfig, setCloudConfig] = useState<CloudConfig | null>(null);
  const [manuals, setManuals] = useState<Manual[]>([]);
  const [connections, setConnections] = useState<CloudConnection[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() => loadCurrentUser());
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("로컬 companion API를 확인하는 중입니다.");
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [form, setForm] = useState({
    title: "새 매뉴얼",
    brand: "",
    model: "",
    tags: "사용법,보관",
    cloudProvider: "local",
    cloudPath: "/ALLManual",
    uploadTarget: "local" as UploadTarget
  });

  const selectedManual = useMemo(
    () => manuals.find((manual) => manual.id === selectedId) ?? manuals[0],
    [manuals, selectedId]
  );
  const isAuthenticated = isRealCurrentUser(currentUser);

  async function refresh() {
    const ownerId = isAuthenticated ? currentUser.ownerId : "";
    const [healthData, manualData, cloudData, configData] = await Promise.all([
      getHealth(),
      ownerId ? getManuals(ownerId) : Promise.resolve([]),
      getCloudConnections(),
      getCloudConfig()
    ]);
    setHealth(healthData);
    setManuals(manualData);
    setConnections(cloudData);
    setCloudConfig(configData);
    setSelectedId((current) => current ?? manualData[0]?.id ?? null);
    setMessage("동기화 완료");
  }

  useEffect(() => {
    loadAdSense();
    refresh().catch((error) => {
      setMessage(`서버 연결 필요: ${error.message}`);
    });
  }, []);

  async function handleSearch(value: string) {
    setQuery(value);
    if (!isAuthenticated) {
      setManuals([]);
      setMessage("Google 또는 Apple 계정으로 먼저 시작하세요.");
      return;
    }
    try {
      const results = value.trim() ? await searchManuals(value, currentUser.ownerId) : await getManuals(currentUser.ownerId);
      setManuals(results);
      setSelectedId(results[0]?.id ?? null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "검색 실패");
    }
  }

  async function handleUpload(event: FormEvent) {
    event.preventDefault();
    if (!uploadFiles?.length) {
      setMessage("매뉴얼 사진을 먼저 선택하세요.");
      return;
    }
    if (!isAuthenticated) {
      setMessage("Google 또는 Apple 계정으로 먼저 시작하세요.");
      return;
    }
    setBusy(true);
    setMessage("매뉴얼을 저장하는 중입니다.");
    try {
      const data = new FormData();
      data.set("title", form.title);
      data.set("brand", form.brand);
      data.set("model", form.model);
      data.set("tags", form.tags);
      data.set("cloudProvider", form.cloudProvider);
      data.set("cloudPath", form.cloudPath);
      data.set("ownerId", currentUser.ownerId);
      data.set("syncTargets", targetProviders(form.uploadTarget).join(","));
      Array.from(uploadFiles).forEach((file) => data.append("images", file));
      const manual = await uploadManual(data);
      setManuals((current) => [manual, ...current.filter((item) => item.id !== manual.id)]);
      setSelectedId(manual.id);
      setMessage("업로드 완료. 한글 모바일 문서로 변환할 수 있습니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "업로드 실패");
    } finally {
      setBusy(false);
    }
  }

  async function handleProcess(manualId: string) {
    setBusy(true);
    setMessage("한글 모바일 문서로 재배치하는 중입니다.");
    try {
      const manual = await processManual(manualId);
      setManuals((current) => current.map((item) => (item.id === manual.id ? manual : item)));
      setSelectedId(manual.id);
      setMessage(health?.openaiConfigured ? "AI 변환 완료" : "샘플 변환 완료: OPENAI_API_KEY 설정 시 실제 변환됩니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "변환 실패");
    } finally {
      setBusy(false);
    }
  }

  async function handleConnect(provider: "google" | "apple") {
    setBusy(true);
    try {
      const connection = await connectCloud(provider, provider === "google" ? "/ALLManual/GoogleDrive" : "/ALLManual/iCloud");
      setConnections((current) => current.map((item) => (item.provider === provider ? connection : item)));
      setMessage(
        connection.status === "connected"
          ? `${providerLabel(provider)} 연결 준비 완료`
          : `${providerLabel(provider)} 설정값이 필요합니다: ${(connection.missing ?? []).join(", ")}`
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "클라우드 연결 실패");
    } finally {
      setBusy(false);
    }
  }

  function targetProviders(target: UploadTarget) {
    if (target === "both") return ["local", "google", "apple"];
    return [target];
  }

  function adapterFor(provider: "google" | "apple"): CloudAdapter {
    if (!cloudConfig) throw new Error("클라우드 설정을 불러오지 못했습니다.");
    if (provider === "google" && cloudConfig.google.enabled) return createGoogleDriveAdapter(cloudConfig.google);
    if (provider === "apple" && cloudConfig.apple.enabled) return createAppleCloudKitAdapter(cloudConfig.apple);
    throw new Error(`${providerLabel(provider)} 설정이 필요합니다.`);
  }

  async function handleCloudSave(provider: "google" | "apple", manual = selectedManual) {
    if (!isAuthenticated) {
      setMessage("Google 또는 Apple 계정으로 먼저 시작하세요.");
      return;
    }
    if (!manual) {
      setMessage("먼저 매뉴얼을 선택하세요.");
      return;
    }
    setBusy(true);
    try {
      const adapter = adapterFor(provider);
      await adapter.connect();
      const ref = await adapter.uploadManual(manual);
      const updated = await saveSyncNote(manual.id, ref);
      setManuals((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSelectedId(updated.id);
      setMessage(`${providerLabel(provider)} 저장 완료`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${providerLabel(provider)} 저장 실패`);
    } finally {
      setBusy(false);
    }
  }

  async function handleCloudImport(provider: "google" | "apple") {
    if (!isAuthenticated) {
      setMessage("Google 또는 Apple 계정으로 먼저 시작하세요.");
      return;
    }
    setBusy(true);
    try {
      const adapter = adapterFor(provider);
      await adapter.connect();
      const cloudManuals = await adapter.listManuals();
      if (!cloudManuals.length) {
        setMessage(`${providerLabel(provider)}에서 가져올 매뉴얼이 없습니다.`);
        return;
      }
      const result = await importManuals(cloudManuals.map((manual) => ({ ...manual, ownerId: currentUser.ownerId })), currentUser.ownerId);
      await refresh();
      setMessage(`${providerLabel(provider)}에서 ${result.imported.length}개 매뉴얼을 가져왔습니다.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${providerLabel(provider)} 동기화 실패`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(provider: "google" | "apple") {
    await adapterFor(provider).disconnect();
    setMessage(`${providerLabel(provider)} 브라우저 세션 연결을 해제했습니다.`);
  }

  async function handleSignIn(provider: "google" | "apple") {
    setBusy(true);
    try {
      const user = provider === "google" ? await signInWithGoogle(cloudConfig?.google) : await signInWithApple(cloudConfig?.apple);
      setCurrentUser(user);
      const userManuals = await getManuals(user.ownerId);
      setManuals(userManuals);
      setSelectedId(userManuals[0]?.id ?? null);
      setMessage(`${user.displayName} 계정으로 시작했습니다.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${providerLabel(provider)} 로그인 실패`);
    } finally {
      setBusy(false);
    }
  }

  function handleSignOut() {
    clearCurrentUser();
    setCurrentUser(null);
    setManuals([]);
    setSelectedId(null);
    setMessage("로그아웃했습니다. Google 또는 Apple 계정으로 다시 시작하세요.");
  }

  function statusText(connection: CloudConnection) {
    if (connection.status === "connected") return "실연동 가능";
    return `설정 필요: ${(connection.missing ?? []).join(", ")}`;
  }

  function providerReady(provider: "google" | "apple") {
    return provider === "google" ? Boolean(cloudConfig?.google.enabled) : Boolean(cloudConfig?.apple.enabled);
  }

  function disabledReason(provider: "google" | "apple") {
    const missing = provider === "google" ? cloudConfig?.google.missing : cloudConfig?.apple.missing;
    return missing?.length ? `설정 필요: ${missing.join(", ")}` : "";
  }

  return (
    <main className="shell">
      <section className="phone">
        <header className="topbar">
          <div>
            <p className="label">세상의 모든 매뉴얼</p>
            <h1>ALLManual</h1>
          </div>
          <div className={health?.ok ? "status online" : "status"}>
            <span />
            {health?.ok ? "API Online" : "API Check"}
          </div>
        </header>

        <section className="searchPanel">
          <div className="searchBox">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => handleSearch(event.target.value)}
              placeholder="제품명, 제조사, 부품명, OCR 텍스트 검색"
            />
          </div>
          <p className="serverLine">{message}</p>
        </section>

        <section className="authStrip" aria-label="회원 상태">
          <div>
            <ShieldCheck size={18} />
            <span>{isAuthenticated ? currentUser.displayName : "계정 로그인"}</span>
          </div>
          {isAuthenticated ? (
            <>
              <small>{providerLabel(currentUser.provider)}</small>
              <button type="button" onClick={handleSignOut}>로그아웃</button>
            </>
          ) : (
            <>
              <button type="button" disabled={busy || !providerReady("google")} title={disabledReason("google")} onClick={() => handleSignIn("google")}>Google로 시작</button>
              <button type="button" disabled={busy || !providerReady("apple")} title={disabledReason("apple")} onClick={() => handleSignIn("apple")}>Apple로 시작</button>
            </>
          )}
        </section>

        <section className="cloudGrid" aria-label="클라우드 연결">
          {connections.map((connection) => (
            <article className="cloudCard" key={connection.provider}>
              <div className="cloudTitle">
                <Cloud size={18} />
                <strong>{connection.displayName}</strong>
              </div>
              <p>{connection.rootPath}</p>
              <div className={`cloudState ${connection.status}`}>
                {connection.status === "connected" ? <Check size={14} /> : <FolderSync size={14} />}
                {connection.provider === "local" ? "연결 가능" : statusText(connection)}
              </div>
              {connection.provider !== "local" && (
                <div className="cloudActions">
                  <button
                    className="ghostButton"
                    type="button"
                    disabled={busy || !providerReady(connection.provider as "google" | "apple")}
                    onClick={() => handleConnect(connection.provider as "google" | "apple")}
                  >
                    경로 지정
                  </button>
                  <button className="ghostButton" type="button" disabled={busy || !isAuthenticated || !providerReady(connection.provider as "google" | "apple")} onClick={() => handleCloudSave(connection.provider as "google" | "apple")}>
                    클라우드 저장
                  </button>
                  <button className="iconButton" type="button" disabled={busy || !isAuthenticated || !providerReady(connection.provider as "google" | "apple")} onClick={() => handleCloudImport(connection.provider as "google" | "apple")} aria-label={`${connection.displayName} 동기화`}>
                    <RefreshCw size={15} />
                  </button>
                  <button className="iconButton" type="button" disabled={busy || !providerReady(connection.provider as "google" | "apple")} onClick={() => handleDisconnect(connection.provider as "google" | "apple")} aria-label={`${connection.displayName} 연결 해제`}>
                    <LogOut size={15} />
                  </button>
                </div>
              )}
            </article>
          ))}
        </section>

        <form className="uploadPanel" onSubmit={handleUpload}>
          <div className="panelTitle">
            <UploadCloud size={20} />
            <h2>매뉴얼 사진/PDF 추가</h2>
          </div>
          <div className="fieldGrid">
            <label>
              제목
              <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            </label>
            <label>
              제조사
              <input value={form.brand} onChange={(event) => setForm({ ...form, brand: event.target.value })} placeholder="예: Panasonic" />
            </label>
            <label>
              모델명
              <input value={form.model} onChange={(event) => setForm({ ...form, model: event.target.value })} placeholder="예: MX-1000" />
            </label>
            <label>
              태그
              <input value={form.tags} onChange={(event) => setForm({ ...form, tags: event.target.value })} />
            </label>
          </div>
          <div className="targetPicker" aria-label="저장 대상">
            {[
              ["local", "Local"],
              ["google", "Google Drive"],
              ["apple", "Apple iCloud"],
              ["both", "둘 다"]
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={form.uploadTarget === value ? "targetButton active" : "targetButton"}
                onClick={() => setForm({ ...form, uploadTarget: value as UploadTarget, cloudProvider: value === "both" ? "local" : value })}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="dropZone">
            <FileImage size={24} />
            <span>{uploadFiles?.length ? `${uploadFiles.length}개 파일 선택됨` : "사진/PDF 선택 또는 촬영"}</span>
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={(event: ChangeEvent<HTMLInputElement>) => setUploadFiles(event.target.files)}
            />
          </label>
          <button className="primaryButton" type="submit" disabled={busy || !isAuthenticated}>
            {busy ? <Loader2 className="spin" size={18} /> : <Sparkles size={18} />}
            저장하기
          </button>
        </form>

        <section className="manualList" aria-label="매뉴얼 목록">
          <div className="sectionHeading">
            <BookOpen size={19} />
            <h2>보관된 매뉴얼</h2>
          </div>
          {manuals.length === 0 && <p className="empty">아직 저장된 매뉴얼이 없습니다.</p>}
          {manuals.map((manual) => (
            <button
              type="button"
              className={manual.id === selectedManual?.id ? "manualRow active" : "manualRow"}
              key={manual.id}
              onClick={() => setSelectedId(manual.id)}
            >
              <span>{manual.title}</span>
              <small>
                {manual.brand || "브랜드 미지정"} · {providerLabel(manual.cloudProvider)} · {formatDate(manual.updatedAt)}
                {manual.lastSyncedAt ? ` · 동기화 ${formatDate(manual.lastSyncedAt)}` : ""}
              </small>
            </button>
          ))}
        </section>

        {selectedManual && (
          <section className="detailPanel">
            <div className="detailHeader">
              <div>
                <p className="label">{selectedManual.brand || "Manual"}</p>
                <h2>{selectedManual.title}</h2>
                <span>{selectedManual.model || "모델 미지정"} · {selectedManual.cloudPath}</span>
              </div>
              <button className="primaryButton compact" type="button" disabled={busy || !isAuthenticated} onClick={() => handleProcess(selectedManual.id)}>
                {busy ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
                변환
              </button>
            </div>
            <div className="syncBar">
              <button type="button" disabled={busy || !isAuthenticated || !providerReady("google")} onClick={() => handleCloudSave("google")}>Google 저장</button>
              <button type="button" disabled={busy || !isAuthenticated || !providerReady("apple")} onClick={() => handleCloudSave("apple")}>Apple 저장</button>
              <span>
                {selectedManual.remoteRefs?.length
                  ? selectedManual.remoteRefs.map((ref) => `${providerLabel(ref.provider)} ${ref.mode}`).join(" · ")
                  : "원격 저장 전"}
              </span>
            </div>

            <div className="imageRail">
              {selectedManual.originalImages.map((image) => (
                <img key={image} src={`${API_ORIGIN}${image}`} alt={`${selectedManual.title} 원본`} />
              ))}
            </div>

            <div className="processedStack">
              {selectedManual.processedPages.length === 0 && (
                <div className="emptyResult">
                  <ImageIcon size={22} />
                  <p>변환 버튼을 누르면 원본 이미지를 보존하면서 한글 모바일 문서 레이아웃을 생성합니다.</p>
                </div>
              )}
              {selectedManual.processedPages.map((page) => (
                <article className="processedPage" key={page.pageNumber}>
                  <div className="pageNumber">PAGE {page.pageNumber}</div>
                  <div dangerouslySetInnerHTML={{ __html: page.layoutHtml }} />
                  {page.generatedImageUrl && (
                    <img className="generated" src={`${API_ORIGIN}${page.generatedImageUrl}`} alt={`AI 재배치 페이지 ${page.pageNumber}`} />
                  )}
                  {page.preservedImages.map((image) => (
                    <img className="preserved" key={image} src={`${API_ORIGIN}${image}`} alt={`보존 이미지 ${page.pageNumber}`} />
                  ))}
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="adSlot" aria-label="광고">
          {adsenseClient ? (
            <ins className="adsbygoogle" data-ad-client={adsenseClient} data-ad-format="auto" data-full-width-responsive="true" />
          ) : (
            <span>Google AdSense 슬롯 · VITE_ADSENSE_CLIENT 설정 시 활성화</span>
          )}
        </section>
      </section>
    </main>
  );
}
